/* @jsx jsx */
import React from 'react';
import uuid from 'uuid/v4';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
import { ReactRenderer } from '@atlaskit/renderer';
import { css, jsx } from '@emotion/react';
import { getSlice } from '../prebuilt/config-items-range/utils';
import { getDiffObjectsBetweenParagraphs } from '../utils/diff-match-patch/utils';
const RED_SUBSTITUTE_HEX = '#AAAAA0';
const BLUE_SUBSTITUTE_HEX = '#AAAAA1';

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- needs manual remediation
const styles = css`
	span[data-text-custom-color='${RED_SUBSTITUTE_HEX}'] {
		background-color: ${"var(--ds-background-accent-red-subtlest, #FFEBE6)"};
		text-decoration: line-through;

		--custom-palette-color: inherit !important;
		user-select: none;
	}
	span[data-text-custom-color='${BLUE_SUBSTITUTE_HEX}'] {
		background-color: ${"var(--ds-background-accent-blue-subtlest, #DEEBFF)"};
		text-decoration: underline;
		text-decoration-style: dashed;

		--custom-palette-color: inherit !important;
	}
`;

/**
 * Helper function to trim empty paragraphs as there isn't much value in
 * diffing empty paragraphs.
 */
function trimEmptyParagraphs(paragraphs) {
  if (!paragraphs.length) {
    return paragraphs;
  }
  const trimmedParagraphs = [...paragraphs];
  while (trimmedParagraphs.length > 0 && trimmedParagraphs[0].text === '') {
    trimmedParagraphs.shift();
  }
  while (trimmedParagraphs.length > 0 && trimmedParagraphs[trimmedParagraphs.length - 1].text === '') {
    trimmedParagraphs.pop();
  }
  return trimmedParagraphs;
}
const jsonTransformer = new JSONTransformer();
export function createDiffPreview({
  editorView,
  pmFragment,
  positions,
  providerFactory
}) {
  var _schema$marks, _content$firstChild, _content$lastChild;
  const {
    schema
  } = editorView.state;

  // If schema doesn't have textColor, underline, or strike, we won't be able to render the highlights
  if (!((_schema$marks = schema.marks) !== null && _schema$marks !== void 0 && _schema$marks.textColor)) {
    return;
  }
  const slice = getSlice(editorView, positions);
  if (slice.openStart !== slice.openEnd) {
    return;
  }
  const paragraphType = schema.nodes.paragraph;
  let content = slice.content;

  /**
   * Edge case for when the slice is a single line - the content will begin and end
   * with inline nodes - to make it convertable to a document we wrap it in a paragraph
   */
  if ((_content$firstChild = content.firstChild) !== null && _content$firstChild !== void 0 && _content$firstChild.isInline && (_content$lastChild = content.lastChild) !== null && _content$lastChild !== void 0 && _content$lastChild.isInline) {
    const wrappedContent = schema.nodes.doc.createAndFill({}, schema.nodes.paragraph.create({}, content));
    if (wrappedContent) {
      content = wrappedContent;
    }
  }
  if (!content) {
    return;
  }
  const inputParagraphs = [];
  content.descendants((node, pos) => {
    if (node.type === paragraphType) {
      const inlineNodePositions = [];
      node.descendants((node, pos) => {
        if (node.isInline && !node.isText) {
          inlineNodePositions.push(pos);
        }
      });
      inputParagraphs.push({
        id: uuid(),
        text: node.textContent,
        from: pos,
        to: pos + node.nodeSize,
        inlineNodePositions
      });
    }
  });
  const outputParagraphs = [];
  pmFragment.descendants((node, pos) => {
    if (node.type === paragraphType) {
      const inlineNodePositions = [];
      node.descendants((node, pos) => {
        if (node.isInline && !node.isText) {
          inlineNodePositions.push(pos);
        }
      });
      outputParagraphs.push({
        id: uuid(),
        text: node.textContent,
        from: pos,
        to: pos + node.nodeSize,
        inlineNodePositions
      });
    }
  });
  const trimmedInputParagraphs = trimEmptyParagraphs(inputParagraphs);
  const trimmedOutputParagraphs = trimEmptyParagraphs(outputParagraphs);
  if (trimmedInputParagraphs.length !== trimmedOutputParagraphs.length) {
    return;
  }
  const diffObjects = getDiffObjectsBetweenParagraphs(trimmedOutputParagraphs, trimmedInputParagraphs);
  const docFrom = schema.nodes.doc.createAndFill({}, pmFragment);
  if (!docFrom) {
    return;
  }
  const transform = new Transform(docFrom);
  let offset = 0;
  diffObjects.forEach(({
    from,
    to,
    text,
    type
  }) => {
    switch (type) {
      case 'ADDITION':
      case 'REPLACEMENT':
        {
          transform.addMark(offset + from, offset + to, schema.marks.textColor.create({
            color: BLUE_SUBSTITUTE_HEX
          }));
          return;
        }
      case 'DELETION':
        {
          transform.insert(offset + to, schema.text(text));
          transform.addMark(offset + to, offset + to + text.length, schema.marks.textColor.create({
            color: RED_SUBSTITUTE_HEX
          }));
          offset += text.length;
          return;
        }
      default:
        return;
    }
  });
  let document;
  try {
    document = jsonTransformer.encode(transform.doc);
  } catch (e) {}
  if (!document) {
    return;
  }
  return jsx("div", {
    "data-testid": "diff-renderer",
    css: styles
  }, jsx(ReactRenderer, {
    document: document,
    adfStage: "stage0",
    media: {
      // We don't currently support images with captions
      allowLinking: false
    },
    schema: schema
    // By default -- the renderer uses a "default schema" which supports all
    // the nodes and marks the markdown transformer can create.
    // see packages/editor/adf-schema/src/schema/default-schema.ts for details.
    ,
    useSpecBasedValidator: true
    // this can only be safely removed once products have upgraded to a compatible version of renderer.
    // @ts-ignore
    ,
    dataProviders: providerFactory
  }));
}